<template>
  <div></div>
</template>
<script>
import { resolveAttributeValue, effectiveManualValue } from './declarationsService.js'
import { initPdfMm, initPdfMmLandscape } from '@/modules/all-reports/pdf/config.js'

export default {
  name: 'DeclarationLabelPrintTable',
  props: {
    entries: { type: Array, required: true },
    settings: { type: Object, required: true }
  },
  methods: {
    printValue (item, field) {
      return field.type === 'attribute' ? resolveAttributeValue(item, field.attribute_field) : effectiveManualValue(field)
    },
    columnLines (entry, column) {
      return entry.fields
        .filter(f => f.column === column && f.printaj && !f.removed)
        .sort((a, b) => a.order - b.order)
        .map(f => ({ label: f.label, value: this.printValue(entry.item, f) }))
        .filter(row => row.value !== '')
    },
    drawLines (doc, lines, x, startY, maxWidth) {
      const { label_font_size: labelFontSize, label_bold: labelBold, value_font_size: valueFontSize, value_bold: valueBold, line_spacing_mm: lineSpacingMm } = this.settings
      // jsPDF wraps a too-long label/value into multiple lines internally
      // using its OWN line height (fontSize converted to the doc's unit,
      // times its line-height factor) — that spacing is not user-adjustable
      // and must not be re-added on top of `line_spacing_mm`, or a wrapped
      // field's tail line ends up sitting well above where the reserved
      // space assumed, leaving a visible gap before the next field.
      const wrapLineHeight = Math.max(labelFontSize, valueFontSize) / doc.internal.scaleFactor * doc.getLineHeightFactor()
      const fieldGap = Math.max(labelFontSize, valueFontSize) * 0.35 + lineSpacingMm
      let lineY = startY
      lines.forEach(line => {
        doc.setFontSize(labelFontSize)
        doc.setFont(labelBold ? 'Roboto-Bold' : 'Roboto-Regular', labelBold ? 'bold' : 'normal')
        const labelText = `${line.label}: `
        const labelWidth = doc.getTextWidth(labelText)
        doc.text(labelText, x, lineY, { maxWidth })

        doc.setFontSize(valueFontSize)
        doc.setFont(valueBold ? 'Roboto-Bold' : 'Roboto-Regular', valueBold ? 'bold' : 'normal')
        const valueText = String(line.value)
        const besideWidth = maxWidth - labelWidth

        // Try to fit the start of the value beside the label; whatever
        // doesn't fit continues on FULL-WIDTH line(s) below, starting at
        // the column's own left edge (x) — not indented past the label the
        // way jsPDF's own single-call wrapping would draw every
        // continuation line. If the label alone eats too much of the
        // column's width, skip straight to the full-width line below (or
        // jsPDF wraps the value one character per line into a near-zero
        // width sliver).
        let extraLines = 0
        if (besideWidth >= maxWidth * 0.3) {
          const narrowLines = doc.splitTextToSize(valueText, besideWidth)
          const firstLine = narrowLines[0] || ''
          doc.text(firstLine, x + labelWidth, lineY, { maxWidth: besideWidth })
          const remainder = valueText.slice(firstLine.length).trim()
          if (remainder) {
            const wideLines = doc.splitTextToSize(remainder, maxWidth)
            doc.text(wideLines, x, lineY + wrapLineHeight, { maxWidth })
            extraLines = wideLines.length
          }
        } else {
          doc.text(valueText, x, lineY + wrapLineHeight, { maxWidth })
          extraLines = doc.splitTextToSize(valueText, maxWidth).length
        }

        lineY += extraLines * wrapLineHeight + fieldGap
      })
    },
    async generate () {
      const doc = this.settings.orientation === 'landscape' ? await initPdfMmLandscape() : await initPdfMm()
      // Slightly wider than jsPDF's default (1.15) — only affects spacing
      // BETWEEN a wrapped field's own continuation lines (e.g. the 2nd line
      // of a long "Naziv"), not the gap to the next field (that's the
      // separate `line_spacing_mm` setting, applied once per field).
      doc.setLineHeightFactor(1.35)
      const pageHeight = doc.internal.pageSize.getHeight()
      const {
        box_width_mm: boxW,
        box_height_mm: boxH,
        columns,
        h_gap_mm: hGap,
        v_gap_mm: vGap,
        margin_top_mm: marginTop,
        margin_left_mm: marginLeft,
        border,
        border_style: borderStyle
      } = this.settings

      const rowsPerPage = Math.max(1, Math.floor((pageHeight - marginTop) / (boxH + vGap)))

      let col = 0
      let row = 0

      this.entries.forEach((entry, idx) => {
        const x = marginLeft + col * (boxW + hGap)
        const y = marginTop + row * (boxH + vGap)

        if (border) {
          doc.setDrawColor(38, 50, 56)
          doc.setLineWidth(0.2)
          if (borderStyle === 'rounded') {
            doc.roundedRect(x, y, boxW, boxH, 2, 2)
          } else {
            doc.rect(x, y, boxW, boxH)
          }
        }

        const col1 = this.columnLines(entry, 1)
        const col2 = this.columnLines(entry, 2)
        const lineYStart = y + 3.5

        if (col1.length > 0 && col2.length > 0) {
          const halfWidth = (boxW - 4) / 2
          this.drawLines(doc, col1, x + 2, lineYStart, halfWidth)
          this.drawLines(doc, col2, x + 3 + halfWidth, lineYStart, halfWidth)
        } else {
          this.drawLines(doc, col1.length > 0 ? col1 : col2, x + 2, lineYStart, boxW - 3)
        }

        col++
        if (col >= columns) {
          col = 0
          row++
        }
        if (row >= rowsPerPage) {
          row = 0
          col = 0
          const isLast = idx === this.entries.length - 1
          if (!isLast) doc.addPage()
        }
      })

      return doc.output('datauristring').split(',')[1]
    }
  }
}
</script>
